import { useEffect, useState } from "react";
import {
  Upload,
  Button,
  Steps,
  Card,
  Space,
  Table,
  Tag,
  Radio,
  Typography,
  Alert,
  UploadFile,
  Row,
  Col,
} from "antd";
import type { RcFile } from "antd/es/upload";
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import saveAs from "file-saver";
import * as ExcelJS from "exceljs";
import "./index.css";
import { getApi, postApi } from "redux/apis";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { getWarrantyDetailsActionTable } from "redux/action/productWarrantyDetailsAction";
import { toastText } from "utils/utils";
import dayjs from "dayjs";
import { downloadServerFile, readBlobError } from "./downloadFile";

const { Text } = Typography;

interface MultiInvoiceFlowProps {
  onClose: () => void;
  onChangeType: () => void;
  searchParams: { fields: string[]; value: string };
  pageSize: number;
  setIsLoading: (visible: boolean) => void;
  currentPage: number;
}

interface PreviewRow {
  rowNumber: number;
  customerName: string;
  invoiceNumber: string;
  invoiceDate: string;
  item: string;
  serial: string;
  status: "new" | "update" | "failed";
  reason?: string;
}

// Rows are not returned with the summary; they are paged from / downloaded via `resultId`.
interface ImportResults {
  resultId: string | null;
  total: number;
  imported: number;
  updated: number;
  failed: number;
}

type StatusFilter = "all" | "new" | "update" | "failed";

const EMPTY_RESULTS: ImportResults = {
  resultId: null,
  total: 0,
  imported: 0,
  updated: 0,
  failed: 0,
};

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const IMPORT_URL = "/productWarrantyDetails/import-serials";

const styleHeaderRow = (worksheet: ExcelJS.Worksheet) => {
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD3D3D3" },
    };
  });
};

const SummaryCard = ({
  value,
  label,
  color,
  background,
  border,
  size = 36,
}: {
  value: number;
  label: string;
  color: string;
  background: string;
  border: string;
  size?: number;
}) => (
  <Card style={{ backgroundColor: background, border: `1px solid ${border}` }}>
    <div style={{ textAlign: "center" }}>
      <div
        style={{ fontSize: size, fontWeight: "bold", color, marginBottom: 8 }}
      >
        {value}
      </div>
      <div style={{ fontSize: 14, color: "#8c8c8c" }}>{label}</div>
    </div>
  </Card>
);

const MultiInvoiceFlow = ({
  onClose,
  onChangeType,
  setIsLoading,
  searchParams,
  pageSize,
  currentPage,
}: MultiInvoiceFlowProps) => {
  // 0 = Upload File, 1 = Preview & Confirm, 2 = Import Results
  const [currentStep, setCurrentStep] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportResults>(EMPTY_RESULTS);
  const [importResults, setImportResults] =
    useState<ImportResults>(EMPTY_RESULTS);
  const [tableRows, setTableRows] = useState<PreviewRow[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const dispatch = useDispatch<AppDispatch>();

  const buildFormData = (): FormData | null => {
    const uploaded = fileList[0]?.originFileObj;
    if (!uploaded) {
      toastText("Please upload an Excel file", "error");
      return null;
    }
    const formData = new FormData();
    formData.append("file", uploaded);
    return formData;
  };

  const downloadSampleFile = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("SampleData");

    const data: string[][] = [
      ["Customer Name", "Invoice Number", "Invoice Date", "Item", "Serial"],
      ["Customer 1", "100", "2026-08-01", "CD350", "00008532110051067154"],
      ["Customer 1", "100", "2026-08-01", "RPT004", "00008532110051067373"],
      ["Customer 2", "101", "2026-08-02", "SC050", "00008532110051066846"],
    ];
    data.forEach((row) => {
      worksheet.addRow(row);
    });

    styleHeaderRow(worksheet);
    worksheet.columns = [
      { width: 24 },
      { width: 18 },
      { width: 16 },
      { width: 22 },
      { width: 28 },
    ];
    // Text format so Excel does not turn long serials / invoice numbers into numbers.
    [1, 2, 3, 4, 5].forEach((c) => {
      worksheet.getColumn(c).numFmt = "@";
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: XLSX_MIME }),
      "3nStar Import Serials.xlsx"
    );
  };

  const handleFileUpload = (file: RcFile): boolean => {
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      toastText("You can only upload Excel file!", "error");
      return false;
    }

    const uploadFile: UploadFile = {
      uid: `${Date.now()}-${file.name}`,
      name: file.name,
      status: "done",
      originFileObj: file,
      size: file.size,
      type: file.type,
    };
    setFileError(null);
    setFileList([uploadFile]);
    return false;
  };

  const generateErrorReport = async () => {
    if (!importResults.resultId || importResults.failed === 0) {
      toastText("No errors to download", "info");
      return;
    }
    try {
      // Built by the server from every failed row, however many there are.
      await downloadServerFile(
        `/productWarrantyDetails/import-results/${importResults.resultId}/failed-records`,
        `failed_records_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`
      );
    } catch (error) {
      toastText(await readBlobError(error), "error");
    }
  };

  // Server-side validation only; nothing is saved.
  const handleValidate = async () => {
    const formData = buildFormData();
    if (!formData) return;

    setIsValidating(true);
    setFileError(null);
    try {
      const response = await postApi(
        `${IMPORT_URL}?preview=true`,
        formData,
        true
      );
      const data: ImportResults = response.data?.data ?? response.data;
      setPreview({ ...EMPTY_RESULTS, ...data });
      setTableRows([]);
      setTableTotal(0);
      setStatusFilter("all");
      setPagination((prev) => ({ current: 1, pageSize: prev.pageSize }));
      setCurrentStep(1);
    } catch (error: any) {
      // 400 = the whole file was rejected (wrong type, empty, missing headers).
      setFileError(
        error?.response?.data?.message ||
          "Something went wrong while validating the file"
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmImport = async () => {
    const formData = buildFormData();
    if (!formData) return;

    setIsImporting(true);
    setIsLoading(true);
    try {
      const response = await postApi(IMPORT_URL, formData, true);
      const data: ImportResults = response.data?.data ?? response.data;
      setImportResults({ ...EMPTY_RESULTS, ...data });

      if (data.imported + data.updated > 0) {
        dispatch(
          getWarrantyDetailsActionTable({
            pageNumber: currentPage,
            pageSize: pageSize,
            searchParams: searchParams,
          })
        );
      }
      setCurrentStep(2);
    } catch (error: any) {
      toastText(
        error?.response?.data?.message || "Error importing serial numbers",
        "error"
      );
    } finally {
      setIsImporting(false);
      setIsLoading(false);
    }
  };

  // Load the current page of preview rows from the server.
  useEffect(() => {
    if (currentStep !== 1 || !preview.resultId) return;
    let cancelled = false;
    const load = async () => {
      setTableLoading(true);
      try {
        const res = await getApi(
          `/productWarrantyDetails/import-results/${preview.resultId}`,
          {
            page: pagination.current,
            pageSize: pagination.pageSize,
            status: statusFilter,
          }
        );
        const data = res.data?.data ?? res.data;
        if (!cancelled) {
          setTableRows(data.rows ?? []);
          setTableTotal(data.totalRecords ?? 0);
        }
      } catch (error: any) {
        if (!cancelled) {
          toastText(
            error?.response?.data?.message || "Unable to load preview rows",
            "error"
          );
        }
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentStep, preview.resultId, pagination, statusFilter]);

  const uploadProps = {
    name: "file",
    multiple: false,
    beforeUpload: handleFileUpload,
    onRemove: () => {
      setFileList([]);
    },
    fileList: fileList,
    accept: ".xlsx,.xls",
  };

  const steps = [
    { title: "Upload File" },
    { title: "Preview & Confirm" },
    { title: "Import Results" },
  ];

  const previewColumns = [
    { title: "ROW #", dataIndex: "rowNumber", key: "rowNumber", width: 80 },
    { title: "CUSTOMER NAME", dataIndex: "customerName", key: "customerName" },
    { title: "INVOICE NUMBER", dataIndex: "invoiceNumber", key: "invoiceNumber" },
    { title: "INVOICE DATE", dataIndex: "invoiceDate", key: "invoiceDate" },
    { title: "ITEM", dataIndex: "item", key: "item" },
    { title: "SERIAL", dataIndex: "serial", key: "serial" },
    {
      title: "STATUS",
      key: "status",
      render: (_: unknown, row: PreviewRow) =>
        row.status === "new" ? (
          <Tag color="success">New</Tag>
        ) : row.status === "update" ? (
          <Tag color="processing">Update</Tag>
        ) : (
          <Tag color="error" style={{ whiteSpace: "normal" }}>
            {row.reason}
          </Tag>
        ),
    },
  ];

  const importableCount = preview.imported + preview.updated;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Alert
              message="Excel must contain 'Customer Name', 'Invoice Number', 'Invoice Date', 'Item' and 'Serial' columns."
              description="One row per serial. A single file can contain multiple customers and invoices. Rows that fail validation are skipped and listed in the failed records report; all other rows are imported."
              type="info"
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 24 }}
            />

            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Text strong style={{ fontSize: 14 }}>
                  Upload Excel File
                </Text>
                <Button
                  type="link"
                  icon={<FileExcelOutlined />}
                  onClick={downloadSampleFile}
                >
                  Download Sample File
                </Button>
              </div>

              {fileList.length === 0 ? (
                <Upload.Dragger {...uploadProps} showUploadList={false}>
                  <p>
                    <UploadOutlined className="ant-upload-drag-icon" />
                  </p>
                  <p className="ant-upload-text">
                    Click to upload or drag and drop
                  </p>
                  <p className="ant-upload-hint">
                    Only .xlsx / .xls files are accepted
                  </p>
                </Upload.Dragger>
              ) : (
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#f0f7ff",
                    borderRadius: "4px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <FileExcelOutlined
                      style={{ fontSize: 24, color: "#1890ff" }}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {fileList[0]?.name || ""}
                      </div>
                      <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                        {fileList[0]?.size
                          ? (fileList[0].size / 1024).toFixed(1)
                          : "0"}{" "}
                        KB
                      </div>
                    </div>
                  </div>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setFileList([]);
                      setFileError(null);
                    }}
                    disabled={isValidating}
                  />
                </div>
              )}

              {fileError && (
                <Alert
                  message={fileError}
                  type="error"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <SummaryCard
                  value={preview.total}
                  label="Total Rows"
                  color="#595959"
                  background="#fafafa"
                  border="#d9d9d9"
                  size={28}
                />
              </Col>
              <Col span={6}>
                <SummaryCard
                  value={preview.imported}
                  label="New"
                  color="#52c41a"
                  background="#f6ffed"
                  border="#b7eb8f"
                  size={28}
                />
              </Col>
              <Col span={6}>
                <SummaryCard
                  value={preview.updated}
                  label="Will be updated"
                  color="#1890ff"
                  background="#f0f7ff"
                  border="#91d5ff"
                  size={28}
                />
              </Col>
              <Col span={6}>
                <SummaryCard
                  value={preview.failed}
                  label="Will be skipped"
                  color="#ff4d4f"
                  background="#fff2f0"
                  border="#ffccc7"
                  size={28}
                />
              </Col>
            </Row>

            <Radio.Group
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ current: 1, pageSize: prev.pageSize }));
              }}
              optionType="button"
              size="small"
              style={{ marginBottom: 12 }}
            >
              <Radio.Button value="all">All</Radio.Button>
              <Radio.Button value="new">New</Radio.Button>
              <Radio.Button value="update">Update</Radio.Button>
              <Radio.Button value="failed">Failed</Radio.Button>
            </Radio.Group>

            <Table
              columns={previewColumns}
              dataSource={tableRows}
              rowKey="rowNumber"
              size="small"
              loading={tableLoading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: tableTotal,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} rows`,
                onChange: (page, size) =>
                  setPagination((prev) => ({
                    current: page,
                    pageSize: size ?? prev.pageSize,
                  })),
              }}
              scroll={{ x: "max-content" }}
              rowClassName={(row: PreviewRow) =>
                row.status === "failed" ? "serial-import-failed-row" : ""
              }
            />

            {preview.failed > 0 && (
              <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 12 }}>
                Failed rows will be skipped; valid rows will still be imported.
              </div>
            )}
          </div>
        );

      case 2: {
        const hasErrors = importResults.failed > 0;
        return (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ marginBottom: 32 }}>
              {hasErrors ? (
                <CloseCircleOutlined
                  style={{ fontSize: 64, color: "#ff4d4f", marginBottom: 16 }}
                />
              ) : (
                <CheckCircleOutlined
                  style={{ fontSize: 64, color: "#52c41a", marginBottom: 16 }}
                />
              )}
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
                {hasErrors
                  ? "Import Completed with Errors"
                  : "Import Completed Successfully"}
              </div>
              <div style={{ fontSize: 14, color: "#8c8c8c" }}>
                Your serial numbers have been processed.
              </div>
            </div>

            <Row gutter={16} style={{ marginBottom: 32 }}>
              <Col span={6}>
                <SummaryCard
                  value={importResults.total}
                  label="Total Rows"
                  color="#595959"
                  background="#fafafa"
                  border="#d9d9d9"
                />
              </Col>
              <Col span={6}>
                <SummaryCard
                  value={importResults.imported}
                  label="Newly Imported"
                  color="#52c41a"
                  background="#f6ffed"
                  border="#b7eb8f"
                />
              </Col>
              <Col span={6}>
                <SummaryCard
                  value={importResults.updated}
                  label="Updated"
                  color="#1890ff"
                  background="#f0f7ff"
                  border="#91d5ff"
                />
              </Col>
              <Col span={6}>
                <SummaryCard
                  value={importResults.failed}
                  label="Failed / Skipped"
                  color="#ff4d4f"
                  background="#fff2f0"
                  border="#ffccc7"
                />
              </Col>
            </Row>

            {hasErrors && (
              <div style={{ marginBottom: 24 }}>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={generateErrorReport}
                  size="large"
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Download Failed Records
                </Button>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      <Steps
        current={currentStep}
        items={steps}
        style={{ marginBottom: 32 }}
      />

      <div style={{ minHeight: 400, marginBottom: 24 }}>
        {renderStepContent()}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          {currentStep === 0 && (
            <Button onClick={onChangeType} size="large">
              Back
            </Button>
          )}
          {currentStep === 1 && (
            <Button
              onClick={() => setCurrentStep(0)}
              size="large"
              disabled={isImporting}
            >
              Back
            </Button>
          )}
        </div>
        <Space>
          {currentStep === 2 ? (
            <Button type="primary" onClick={onClose} size="large">
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={onClose}
                size="large"
                disabled={isValidating || isImporting}
              >
                Cancel
              </Button>
              {currentStep === 0 ? (
                <Button
                  type="primary"
                  onClick={handleValidate}
                  size="large"
                  loading={isValidating}
                  disabled={fileList.length === 0}
                >
                  Validate & Preview
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleConfirmImport}
                  size="large"
                  loading={isImporting}
                  disabled={importableCount === 0}
                >
                  {`Import ${importableCount} Records`}
                </Button>
              )}
            </>
          )}
        </Space>
      </div>
    </>
  );
};

export default MultiInvoiceFlow;
